// KPI — compilazione province clienti da P.IVA (registro VIES) con avanzamento, usata nel mix geografico
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Loader2 } from "lucide-react";
import { fmtInt } from "../bi/biShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LABELS = { etiflor: "dai DDT Etiflor", vies: "da VIES", scadenziario: "dallo scadenziario", senza_piva: "senza P.IVA", piva_non_valida: "P.IVA non attiva in VIES", vies_senza_provincia: "VIES senza provincia", vies_errore: "errore VIES" };

export default function KpiProvinces({ onDone }) {
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [showList, setShowList] = useState(false);
  const job = status?.job;
  const running = job?.status === "running";

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/admin/customers/fill-provinces/status`).then((r) => { if (alive) setStatus(r.data); }).catch(() => {});
    load();
    const t = setInterval(() => { if (alive) load(); }, running ? 2500 : 15000);
    return () => { alive = false; clearInterval(t); };
  }, [running]);

  const prevStatus = useRef(null);
  useEffect(() => {
    if (prevStatus.current === "running" && job?.status === "done" && onDone) onDone();
    prevStatus.current = job?.status;
  }, [job?.status, onDone]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await axios.post(`${API}/admin/customers/fill-provinces`);
      setStatus((s) => ({ ...(s || {}), job: r.data }));
      toast.success("Compilazione province avviata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile avviare la compilazione");
    } finally {
      setStarting(false);
    }
  };

  if (!status) return null;
  const pct = job?.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="bg-white border border-stone-200 rounded-xl p-3 space-y-2" data-testid="kpi-provinces">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs text-stone-600 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[#8A1C2A]" /> <b>{fmtInt(status.missing)}</b> clienti su {fmtInt(status.total)} senza provincia in anagrafica</p>
        <button type="button" onClick={start} disabled={running || starting || status.missing === 0}
          className="h-10 px-3 rounded-xl bg-[#8A1C2A] text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="kpi-provinces-start">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <MapPin className="w-3.5 h-3.5" />} {running ? "In corso…" : "Compila province da P.IVA"}
        </button>
      </div>
      {job && (
        <div className="space-y-1.5" data-testid="kpi-provinces-job">
          <div className="flex items-center gap-2 text-[11px] text-stone-600">
            <div className="flex-1 bg-stone-100 rounded-full h-2 overflow-hidden"><div className="h-full bg-[#8A1C2A] transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            <span className="tabular-nums shrink-0" data-testid="kpi-provinces-progress">{fmtInt(job.done)} / {fmtInt(job.total)} · {fmtInt(job.filled)} compilati</span>
          </div>
          {running && job.current && <p className="text-[11px] text-stone-500 truncate">Sto verificando: {job.current}</p>}
          {job.status === "error" && <p className="text-[11px] text-red-700">Errore: {job.error}</p>}
          {job.status === "done" && (
            <p className="text-[11px] text-stone-600" data-testid="kpi-provinces-results">
              Completato: {Object.entries(job.results || {}).map(([k, v]) => `${fmtInt(v)} ${LABELS[k] || k}`).join(" · ")}.
              {job.unresolved?.length > 0 && <> {fmtInt(job.unresolved.length)} non risolti — <button type="button" className="underline" onClick={() => setShowList((o) => !o)} data-testid="kpi-provinces-unresolved-toggle">{showList ? "nascondi" : "mostra"}</button></>}
            </p>
          )}
          {showList && job.unresolved?.length > 0 && <p className="text-[11px] text-stone-500 max-h-32 overflow-y-auto" data-testid="kpi-provinces-unresolved">{job.unresolved.join(" · ")}</p>}
        </div>
      )}
    </div>
  );
}
